<template>
  <v-row class="flex-column">
    <base-icon-button
      :disabled="!isPublished"
      :button-icon="['fab', 'linkedin']"
      button-color="primary"
      tool-tip="Share to LinkedIn"
      large
      @click="shareLinkedIn"
    ></base-icon-button>
    <v-divider class="my-3" />
    <base-icon-button
      :button-icon="['fas', 'link']"
      button-color="primary"
      tool-tip="Copy link for social media"
      large
      :disabled="!isPublished"
      @click="copyLink"
    ></base-icon-button>
    <v-divider class="my-3" />
    <base-icon-button
      :button-icon="['fas', 'copy']"
      button-color="primary"
      tool-tip="Copy link with Preview"
      large
      :disabled="!isPublished"
      @click="copyRichSnippet"
    ></base-icon-button>
    <v-divider class="my-3" />
    <base-icon-button
      :button-icon="['fad', 'arrow-left']"
      button-color="primary"
      dark
      large
      button-outlined
      tool-tip="Back to Producer"
      @click="backToProducer"
    ></base-icon-button>
    <v-divider class="my-3" />
    <base-icon-button
      :button-icon="['fad', 'edit']"
      button-color="primary"
      dark
      large
      button-outlined
      tool-tip="Edit Video"
      @click="backToEdit"
    ></base-icon-button>
    <v-divider class="my-3" />
    <base-icon-button
      :button-icon="['fad', 'images']"
      button-color="primary"
      button-outlined
      tool-tip="Change Thumbnail"
      large
      @click="selectThumbnail"
    ></base-icon-button>
    <v-divider class="my-3" />
    <base-icon-button
      :button-icon="['fal', 'download']"
      button-color="primary"
      button-outlined
      tool-tip="Download Video"
      :disabled="video.status !== 'COMPLETED'"
      large
      @click="downloadVideo"
    ></base-icon-button>

    <prompt ref="shareLinkedInPrompt">
      <template #body>
        <p v-if="!video.isPublished">
          This action will publish your video allowing anyone with the link to view it. Clicking share will open
          Linkedin in a new tab.
        </p>
        <p v-else>This action will open a new window to share this page to LinkedIn.</p>
        <p>Are you sure you want to continue?</p>
      </template>
    </prompt>
  </v-row>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import Prompt from '../dashboard/Prompt';

export default {
  name: 'SidePanel',
  components: {
    Prompt,
  },
  computed: {
    ...mapFields('video', {
      video: 'current',
      isPublished: 'current.isPublished',
      displayContactDetails: 'current.displayContactDetails',
      videoUrl: 'current.url',
      subtitledUrl: 'current.subtitledUrl',
      user: 'current.user',
    }),
    ...mapGetters('application', ['whiteLabelDomain']),
    landingPageUrl() {
      return this.whiteLabelDomain
        ? `https://producer.${this.whiteLabelDomain}/v/${this.video.shortId}`
        : window.location.href;
    },
  },
  methods: {
    copyLink() {
      this.$copyText(this.landingPageUrl)
        .then(() => {
          this.notifySuccess();
        })
        .catch(() => {
          this.notifyError();
        });
    },
    notifySuccess: _.debounce(
      function () {
        this.$notify({
          group: 'app',
          type: 'success',
          title: 'Success!',
          text: 'Link Copied',
        });
      },
      1500,
      { leading: true, maxWait: 1500 },
    ),
    notifyError: _.debounce(
      function () {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Error!',
          text: 'Link Copy Failed',
        });
      },
      1500,
      { leading: true, maxWait: 1500 },
    ),
    backToEdit() {
      this.$router.push(`/videos/video/${this.video._id}`);
    },
    backToProducer() {
      this.$router.push(`/videos`);
    },
    downloadVideo() {
      const url = this.subtitledUrl || this.videoUrl;
      window.open(url, '_blank');
    },
    selectThumbnail() {
      this.$nuxt.$emit('show-preview', this.video, { selectThumbnail: true });
    },
    async shareLinkedIn() {
      const shareToLinkedIn = await this.$refs.shareLinkedInPrompt.open(`Share Video to LinkedIn`, `Share`);
      if (shareToLinkedIn) {
        this.isPublished = true;
        try {
          await this.$store.dispatch('video/update');
        } catch (err) {
          this.isPublished = false;

          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Error!',
            text: `A problem occurred while trying publish your video: ${err.message}`,
            duration: 5000,
          });
        }
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${this.landingPageUrl}`);
      }
    },
    async loadRichSnippet() {
      return await this.$axios.post(this.$config.visionApiEndpoint + '/api/v1/html-builder/rich-snippet', {
        poster:
          !this.user.hidePlayIconProducer && this.video.thumbnailOverlaidUrl
            ? this.video.thumbnailOverlaidUrl
            : this.video.thumbnail,
        url: this.landingPageUrl,
        title: this.video.metadata.name ? this.video.metadata.name : `Video message from ${this.user.name}`,
        intro: this.video.metadata.description ? this.video.metadata.description : '',
        user: this.user.name,
      });
    },
    async copyRichSnippet() {
      const html = await this.loadRichSnippet();
      const snippet = new this.$clipboard.ClipboardItem({
        'text/html': new Blob([html.data], { type: 'text/html' }),
        'text/plain': new Blob([window.location.href], { type: 'text/plain' }),
      });
      await this.$clipboard.write([snippet]).catch(err => {
        if (err) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Error!',
            text: 'Failed to Copy link with Preview, if this happens again please contact support.',
          });
        }
      });

      this.$notify({
        group: 'app',
        type: 'success',
        title: 'Success!',
        text: 'Copy Link with Preview copied',
      });
    },
  },
};
</script>
